<template>
  <div id="v1-recommendationItineraries" class="v1-recommendationItineraries">
    <transition appear v-if="type === 'filter'">
      <div class="v1-recommendationItineraries__selectbox">
        <Selectbox
          :keys="selectboxKey"
          :values="selectboxValue"
          :selectboxText="selectboxText"
          :condition="conditionParameter"
          @selectArea="selectArea($event)"
        ></Selectbox>
      </div>
    </transition>
    <transition appear>
      <div class="v1-recommendationItineraries__list">
        <ItineraryListItem
          :posts="currentPosts"
          :pageLang="pageLang"
          :pageCountry="pageCountry"
          :context="context"
        ></ItineraryListItem>
      </div>
    </transition>
    <div class="v1-recommendationItineraries__result" :class="{ '-show': !result }">
      <p class="v1-recommendationItineraries__noResults">
        {{ getNoResultText() }}
      </p>
    </div>
    <transition appear>
      <div class="v1-recommendationItineraries__pagination" :class="{ '-hide': !result }">
        <Paginate
          ref="pagination"
          @updatePageNumber="updatePage($event)"
          :pageCount="pageDivisionCount"
          :page="pageParameter"
          v-if="type === 'latest' || type === 'filter'"
        ></Paginate>
      </div>
    </transition>
  </div>
</template>

<script>
import scroller from './v1Scroller';
import ItineraryListItem from './v1ItineraryListItem.vue';
import Paginate from './v1Paginate.vue';
import Selectbox from './v1Selectbox.vue';
import getParameter from '../utils/getParameter';

const { location } = window;
const { pathname } = location;
const pageLang = pathname.split('/')[1];
const pageCountry = pathname.split('/')[2];
const relativePath = pathname.replace(`/${pageLang}`, '').replace(`/${pageCountry}`, '');
const langCode = pageLang === 'zh' ? `${pageLang}-${pageCountry}` : pageLang;

export default {
  name: 'v1ItineraryList',
  components: {
    ItineraryListItem,
    Paginate,
    Selectbox,
  },
  props: {
    // 新着（フィルターなし）、新着（フィルターあり）、ランダム
    type: {
      require: false,
      type: String,
      default: 'random',
      validator(val) {
        return ['latest', 'filter', 'random'].includes(val);
      },
    },
    context: {
      require: false,
      type: String,
      default: 'itinerary',
      validator(val) {
        return ['top', 'itinerary'].includes(val);
      },
    },
  },
  data() {
    return {
      // 取得するjsonの言語
      lang: langCode.toLowerCase(),
      selectboxKey: this.$store.state.prefectureKeys,
      selectboxValue: this.getSelectboxValue(),
      selectboxText: this.getSelectboxText(),
      pageLang,
      pageCountry,
    };
  },
  created() {
    this.setPageParameter();
    this.setConditionParameter();
    this.initParameter();
    this.setRelativePath();
    this.getLangJson(langCode);
    this.getPopstate();
  },
  methods: {
    getSelectboxValue() {
      /* eslint-disable no-nested-ternary */
      const prefValues = this.$store.state.prefectureValues;
      return langCode === 'en'
        ? prefValues.en
        : langCode === 'ja'
        ? prefValues.ja
        : langCode === 'ko'
        ? prefValues.ko
        : langCode === 'zh-cn'
        ? prefValues['zh-cn']
        : langCode === 'zh-tw'
        ? prefValues['zh-tw']
        : langCode === 'de'
        ? prefValues.de
        : langCode === 'fr'
        ? prefValues.fr
        : langCode === 'th'
        ? prefValues.th
        : langCode === 'id'
        ? prefValues.id
        : langCode === 'zh-hk'
        ? prefValues['zh-hk']
        : langCode === 'vi'
        ? prefValues.vi
        : prefValues.en;
      /* eslint-enable */
    },
    getSelectboxText() {
      /* eslint-disable no-nested-ternary */
      const prefSelectboxText = this.$store.state.prefectureSelectboxText;
      return langCode === 'en'
        ? prefSelectboxText.en
        : langCode === 'ja'
        ? prefSelectboxText.ja
        : langCode === 'ko'
        ? prefSelectboxText.ko
        : langCode === 'zh-cn'
        ? prefSelectboxText['zh-cn']
        : langCode === 'zh-tw'
        ? prefSelectboxText['zh-tw']
        : langCode === 'de'
        ? prefSelectboxText.de
        : langCode === 'fr'
        ? prefSelectboxText.fr
        : langCode === 'th'
        ? prefSelectboxText.th
        : langCode === 'id'
        ? prefSelectboxText.id
        : langCode === 'zh-hk'
        ? prefSelectboxText['zh-hk']
        : langCode === 'vi'
        ? prefSelectboxText.vi
        : prefSelectboxText.en;
      /* eslint-enable */
    },
    getNoResultText() {
      /* eslint-disable no-nested-ternary */
      const { noResult } = this.$store.state;
      return langCode === 'en'
        ? noResult.en
        : langCode === 'ja'
        ? noResult.ja
        : langCode === 'ko'
        ? noResult.ko
        : langCode === 'zh-cn'
        ? noResult['zh-cn']
        : langCode === 'zh-tw'
        ? noResult['zh-tw']
        : langCode === 'de'
        ? noResult.de
        : langCode === 'fr'
        ? noResult.fr
        : langCode === 'th'
        ? noResult.th
        : langCode === 'id'
        ? noResult.id
        : langCode === 'zh-hk'
        ? noResult['zh-hk']
        : langCode === 'vi'
        ? noResult.vi
        : noResult.en;
      /* eslint-enable */
    },
    // ルート要素までスムーススクロールする
    scoller() {
      const rootElm = document.getElementById('v1Itinerary');
      scroller.toElement(rootElm);
    },
    // リストのルート要素にフォーカスする
    listFocus() {
      const rootElm = document.getElementById('v1-recommendationItinerariesList');
      rootElm.setAttribute('tabindex', 0);
      rootElm.focus();
      rootElm.removeAttribute('tabindex');
    },
    // ランダム表示では現在ページを出力しないため、URLを取得する
    setRelativePath() {
      this.$store.commit('setRelativePath', relativePath);
    },
    // 現在ページをVuexに保存する
    setPageParameter() {
      const pageParameter = getParameter('page');
      this.$store.commit('setPageParameter', pageParameter);
    },
    // 絞り込みエリアをVuexに保存する
    setConditionParameter() {
      const conditionParameter = getParameter('condition');
      this.$store.commit('setConditionParameter', conditionParameter);
    },
    // パラメーターの初期値を設定する
    initParameter() {
      const { pageParameter } = this.$store.state;
      const { conditionParameter } = this.$store.state;
      const hasParameter = pageParameter ? true : !!conditionParameter;

      if (this.type === 'latest') {
        if (pageParameter) {
          window.history.pushState('', '', `?page=${pageParameter}`);
        } else {
          window.history.pushState('', '', `?page=1`);
        }
      }

      if (this.type === 'filter') {
        if (hasParameter) {
          window.history.pushState(
            '',
            '',
            `?page=${pageParameter}&condition=${conditionParameter}`,
          );
        } else {
          window.history.pushState('', '', `?page=1&condition=${conditionParameter}`);
        }
      }
    },
    // ページネーションか絞り込み実行時にデータを更新する
    updatePage(page) {
      this.$store.commit('setPageParameter', page);
      const conditionParameter = getParameter('condition');
      if (this.type === 'latest') {
        window.history.pushState('', '', `?page=${page}`);
      }

      if (this.type === 'filter') {
        window.history.pushState('', '', `?page=${page}&condition=${conditionParameter}`);
      }

      this.scoller();
      this.listFocus();
    },
    // 対応する言語のjsonファイルを取得する
    getLangJson(lang) {
      this.$store.dispatch('getLangJson', lang);
    },
    // 絞り込み実行時にデータを更新する
    selectArea(area) {
      this.$store.state.conditionParameter = area;
      this.$store.commit('setPageParameter', 1);
      this.$refs.pagination.resetCurrentPages();
      window.history.pushState(
        '',
        '',
        `?page=${this.$store.state.pageParameter}&condition=${this.$store.state.conditionParameter}`,
      );
      this.scoller();
      this.listFocus();
      setTimeout(() => {
        this.$refs.pagination.addCurrent();
      }, 0);
    },
    // ブラウザバック時のパラメーターを出力に反映する
    getPopstate() {
      const self = this;
      window.addEventListener('popstate', () => {
        const pageParameter = getParameter('page');
        const conditionParameter = getParameter('condition');
        if (pageParameter) {
          self.$store.commit('setPageParameter', pageParameter);
        }
        if (conditionParameter) {
          self.$store.commit('setConditionParameter', conditionParameter);
        }

        if (self.$refs.pagination) {
          this.scoller();
          this.listFocus();

          setTimeout(() => {
            self.$refs.pagination.addCurrent();
          }, 0);
        }
      });
    },
  },
  computed: {
    relativePath() {
      return this.$store.state.relativePath;
    },
    posts() {
      return this.$store.state.posts;
    },
    result() {
      return !!this.$store.state.currentPosts.length;
    },
    currentPosts() {
      if (this.type === 'random') {
        return this.$store.getters.randomPosts;
      }
      if (this.type === 'filter') {
        return this.$store.getters.filterPosts;
      }
      return this.$store.getters.latestPosts;
    },
    pageParameter() {
      return Number(this.$store.state.pageParameter);
    },
    conditionParameter() {
      return this.$store.state.conditionParameter;
    },
    pageDivisionCount() {
      return this.$store.getters.pageDivisionCount;
    },
  },
};
</script>

<style lang="scss" scoped>
.v-enter,
.v-leave-to {
  opacity: 0;
}

.v-enter-active,
.v-leave-active {
  transition-timing-function: $transition-timing-function;
  transition-duration: $transition-duration * 4;
  transition-property: opacity;
}
</style>
